import { useState } from "react";

/**
 * Local, editable state that adopts a stored document whenever that document changes.
 *
 * Both export tabs seed a control from `useScheduleExportSettings`, and `useState` captures only its
 * FIRST value — while `UserSettingsProvider` serves the shipped defaults until its query resolves. So a
 * tab mounted before hydration kept showing defaults after the real document arrived, and the first
 * interaction wrote those defaults back over everything the user had saved. A department switch has the
 * same shape: new query data, stale local state.
 *
 * Keyed on the stored document's IDENTITY, not its contents. The provider derives it straight from the
 * query cache with `useMemo`, so it only changes when the data actually changes — an edit made after
 * hydration is not disturbed by unrelated re-renders.
 *
 * Setting state during render is React's sanctioned way to derive from props and is discard-safe; an
 * effect would paint one frame of the wrong values first.
 */
export const useHydratedSettings = <T>(stored: T): [T, (next: T) => void] => {
	const [local, setLocal] = useState<T>(stored);
	const [hydratedFrom, setHydratedFrom] = useState<T>(stored);

	if (hydratedFrom !== stored) {
		setHydratedFrom(stored);
		setLocal(stored);
	}

	return [local, setLocal];
};
